// Componente Toast para notificaciones flotantes.
// Reemplaza los alert() del navegador con una experiencia más moderna.
// Soporta diferentes tipos: success, error, warning, info, loading.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

// Iconos según el tipo de notificación
const icons = {
  success: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
  loading: (
    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  )
};

// Configuración de estilos según el tipo
const typeStyles = {
  success: {
    container: 'bg-green-500 text-white shadow-green-500/30',
    icon: 'text-white'
  },
  error: {
    container: 'bg-red-500 text-white shadow-red-500/30',
    icon: 'text-white'
  },
  warning: {
    container: 'bg-yellow-500 text-white shadow-yellow-500/30',
    icon: 'text-white'
  },
  info: {
    container: 'bg-blue-800 text-white shadow-blue-800/30',
    icon: 'text-white'
  },
  loading: {
    container: 'bg-gray-700 text-white shadow-gray-700/30',
    icon: 'text-white'
  }
};

/**
 * Componente Toast individual
 * Muestra una notificación flotante con animación de entrada/salida
 */
const ToastItem = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const currentStyle = typeStyles[toast.type] || typeStyles.info;

  // Auto-ocultamiento después de la duración especificada.
  // Los toasts de tipo loading no se ocultan automáticamente.
  useEffect(() => {
    if (toast.duration !== 0 && toast.type !== 'loading') {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300); // Espera a que termine la animación
      }, toast.duration || 4000);

      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, toast.type, onRemove]);

  const handleClose = () => {
    // No permitir cerrar manualmente toasts de tipo loading
    if (toast.type === 'loading') return;
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      className={twMerge(
        'flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg backdrop-blur-sm',
        'transition-all duration-300 ease-in-out',
        'animate-in slide-in-from-right fade-in',
        isExiting && 'animate-out slide-out-to-right fade-out',
        currentStyle.container
      )}
    >
      <span className={currentStyle.icon}>
        {icons[toast.type] || icons.info}
      </span>
      
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="font-semibold text-sm">{toast.title}</p>
        )}
        <p className={twMerge('text-sm', toast.title && 'text-white/90')}>
          {toast.message}
        </p>
      </div>

      <button
        onClick={handleClose}
        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
        aria-label="Cerrar notificación"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

/**
 * Contenedor de toasts
 * Muestra todas las notificaciones activas en una posición fija
 */
const ToastContainer = ({ toasts, onRemove, position = 'top-right' }) => {
  const positionStyles = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2 w-full max-w-sm',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 w-full max-w-sm'
  };

  return createPortal(
    <div className={twMerge('fixed z-50 flex flex-col gap-2 pointer-events-none', positionStyles[position])}>
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto mx-4">
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>,
    document.body
  );
};

export default ToastContainer;
